import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import { ROUTES } from "config/navConfig";
import { RoleProvider } from "context/RoleContext";

// ─── Public / Marketing pages ─────────────────────────────────────────────────
import NotFound              from "pages/NotFound";
import HomeLandingPage       from "./pages/home-landing-page";
import PlatformOverviewPage  from "./pages/platform-overview-page";
import HowItWorksPage        from "./pages/how-it-works-page";
import SecurityCompliancePage from "./pages/security-compliance-page";
import AboutPage             from "./pages/about-page";
import ContactPage           from "./pages/contact-page";
import LoginPage             from "./pages/login";

// ─── Dashboard ────────────────────────────────────────────────────────────────
import PortfolioDashboard    from "./pages/portfolio-dashboard";

// ─── Funds ────────────────────────────────────────────────────────────────────
import FundsListPage         from "./pages/funds";
import FundDetailPage        from "./pages/funds/FundDetail";

// ─── Marketplace ──────────────────────────────────────────────────────────────
import OpportunitiesListPage from "./pages/opportunities-list";
import OpportunityDetail     from "./pages/opportunity-detail";
import DealSubmission        from "./pages/deal-submission";

// ─── Deals ────────────────────────────────────────────────────────────────────
import PipelinePage          from "./pages/pipeline";
import DealRecord            from "./pages/deal-record";
import DealStructuringHub    from "./pages/deal-structuring-hub";
import DealStructuringWorkspace from "./pages/deal-structuring-workspace";
import SPVDashboard          from "./pages/spv-dashboard";
import SPVDealsPage          from "./pages/spv-deals";
import SPVCreationWizard     from "./pages/spv-creation-wizard";
import InvestorOnboarding    from "./pages/investor-onboarding";

// ─── Portfolio ────────────────────────────────────────────────────────────────
import PortfolioAssetsPage   from "./pages/portfolio-assets-page";
import AssetDetailPage       from "./pages/asset-detail-page";
import AssetLifecycle        from "./pages/asset-management/AssetLifecycle";
import ValuationView         from "./pages/asset-management/ValuationView";
import PortfolioHeatmap      from "./pages/asset-management/PortfolioHeatmap";
import VintageAnalysis       from "./pages/asset-management/VintageAnalysis";
import AssetDetailDrilldown  from "./pages/asset-management/AssetDetailDrilldown";

// ─── Asset Management ─────────────────────────────────────────────────────────
import OperationsConsole     from "./pages/asset-management/OperationsConsole";
import OversightHub          from "./pages/asset-management/OversightHub";
import ReviewPacks           from "./pages/asset-management/ReviewPacks";
import RiskMonitor           from "./pages/asset-management/RiskMonitor";
import ValueCreation         from "./pages/asset-management/ValueCreation";
import WatchlistActions      from "./pages/asset-management/WatchlistActions";
import ReportBuilder         from "./pages/asset-management/ReportBuilder";

// ─── Operations ───────────────────────────────────────────────────────────────
import DataRoom              from "./pages/data-room";
import ESGReportingDashboard from "./pages/esg-reporting-dashboard";
import GovernanceLibrary     from "./pages/asset-management/GovernanceLibrary";
import CounterpartyDirectory from "./pages/asset-management/CounterpartyDirectory";
import AuditTrailExports     from "./pages/asset-management/AuditTrailExports";

// ─── Transactions ─────────────────────────────────────────────────────────────
import RWATokenisationPage   from "./pages/rwa-tokenisation";
import TokenHolderDashboard  from "./pages/token-holder-dashboard";
import TokenTransferPage     from "./pages/token-transfer";
import TokenAdminPage        from "./pages/token-admin";
import GovernancePage        from "./pages/governance";
import GovernanceAnalyticsPage from "./pages/governance-analytics";
import VoteDelegationPage    from "./pages/vote-delegation";
import GovernanceCompliancePage from "./pages/governance-compliance";

// ─── Communications ───────────────────────────────────────────────────────────
import CommunicationsHub     from "./pages/communications-hub";
import NotificationsPage     from "./pages/notifications";

// ─── New MVP Modules (Role Framework Adaptation) ──────────────────────────────
import ICSubmissionPage      from "./pages/ic-submission";
import DealRoomPage          from "./pages/deal-room";
import ActivityFeedPage      from "./pages/activity-feed";

// ─── Administration ───────────────────────────────────────────────────────────
import TeamManagement        from "./pages/team-management";
import SettingsPage          from "./pages/settings";
import PermissionsPage       from "./pages/admin-permissions";

const Routes = () => {
  return (
    <RoleProvider>
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <RouterRoutes>

          {/* ── Public / Marketing ─────────────────────────────────────────── */}
          <Route path={ROUTES.home}               element={<HomeLandingPage />} />
          <Route path={ROUTES.login}              element={<LoginPage />} />
          <Route path={ROUTES.platformOverview}   element={<PlatformOverviewPage />} />
          <Route path={ROUTES.howItWorks}         element={<HowItWorksPage />} />
          <Route path={ROUTES.securityCompliance} element={<SecurityCompliancePage />} />
          <Route path={ROUTES.about}              element={<AboutPage />} />
          <Route path={ROUTES.contact}            element={<ContactPage />} />

          {/* Legacy public-page redirects (remove -page suffix) */}
          <Route path="/home-landing-page"        element={<Navigate to={ROUTES.home} replace />} />
          <Route path="/platform-overview-page"   element={<Navigate to={ROUTES.platformOverview} replace />} />
          <Route path="/how-it-works-page"        element={<Navigate to={ROUTES.howItWorks} replace />} />
          <Route path="/security-compliance-page" element={<Navigate to={ROUTES.securityCompliance} replace />} />
          <Route path="/about-page"               element={<Navigate to={ROUTES.about} replace />} />
          <Route path="/contact-page"             element={<Navigate to={ROUTES.contact} replace />} />

          {/* ── Dashboard ──────────────────────────────────────────────────── */}
          <Route path={ROUTES.dashboard}          element={<PortfolioDashboard />} />
          {/* Legacy redirect */}
          <Route path="/portfolio-dashboard"      element={<Navigate to={ROUTES.dashboard} replace />} />

          {/* ── Funds ──────────────────────────────────────────────────────── */}
          <Route path={ROUTES.funds}              element={<FundsListPage />} />
          <Route path={ROUTES.fundDetail}         element={<FundDetailPage />} />

          {/* ── Marketplace ────────────────────────────────────────────────── */}
          <Route path={ROUTES.opportunities}      element={<OpportunitiesListPage />} />
          <Route path={ROUTES.opportunityDetail}  element={<OpportunityDetail />} />
          <Route path={ROUTES.dealSubmission}     element={<DealSubmission />} />
          {/* Legacy redirect */}
          <Route path="/opportunities-list"       element={<Navigate to={ROUTES.opportunities} replace />} />
          <Route path="/opportunity-detail"       element={<Navigate to={ROUTES.opportunityDetail} replace />} />

          {/* ── Deals ──────────────────────────────────────────────────────── */}
          <Route path={ROUTES.pipeline}           element={<PipelinePage />} />
          <Route path={ROUTES.dealRecord}         element={<DealRecord />} />
          <Route path={ROUTES.structuringHub}     element={<DealStructuringHub />} />
          <Route path={ROUTES.dealWorkspace}      element={<DealStructuringWorkspace />} />
          <Route path={ROUTES.spvDeals}           element={<SPVDealsPage />} />
          <Route path={ROUTES.spvDashboard}       element={<SPVDashboard />} />
          <Route path={ROUTES.spvCreation}        element={<SPVCreationWizard />} />
          <Route path={ROUTES.investorOnboarding} element={<InvestorOnboarding />} />
          {/* Legacy redirects */}
          <Route path="/pipeline"                 element={<Navigate to={ROUTES.pipeline} replace />} />
          <Route path="/deal-structuring-hub"     element={<Navigate to={ROUTES.structuringHub} replace />} />
          <Route path="/deal-structuring-workspace" element={<Navigate to={ROUTES.dealWorkspace} replace />} />
          <Route path="/spv-dashboard"            element={<Navigate to={ROUTES.spvDashboard} replace />} />
          <Route path="/spv-creation-wizard"      element={<Navigate to={ROUTES.spvCreation} replace />} />
          <Route path="/investor-onboarding"      element={<Navigate to={ROUTES.investorOnboarding} replace />} />

          {/* ── Portfolio ──────────────────────────────────────────────────── */}
          <Route path={ROUTES.portfolioAssets}    element={<PortfolioAssetsPage />} />
          <Route path={ROUTES.assetDetail}        element={<AssetDetailDrilldown />} />
          <Route path={ROUTES.assetLifecycle}     element={<AssetLifecycle />} />
          <Route path={ROUTES.valuationView}      element={<ValuationView />} />
          <Route path={ROUTES.portfolioHeatmap}   element={<PortfolioHeatmap />} />
          <Route path={ROUTES.vintageAnalysis}    element={<VintageAnalysis />} />
          {/* Legacy redirects */}
          <Route path="/portfolio-assets-page"    element={<Navigate to={ROUTES.portfolioAssets} replace />} />
          <Route path="/asset-detail-page"        element={<Navigate to={ROUTES.portfolioAssets} replace />} />
          <Route path="/asset-management/portfolio-heatmap"  element={<Navigate to={ROUTES.portfolioHeatmap} replace />} />
          <Route path="/asset-management/asset-lifecycle"    element={<Navigate to={ROUTES.assetLifecycle} replace />} />
          <Route path="/asset-management/vintage-analysis"   element={<Navigate to={ROUTES.vintageAnalysis} replace />} />
          <Route path="/asset-management/valuation-view"     element={<Navigate to={ROUTES.valuationView} replace />} />
          <Route path="/asset-management/asset-detail/:assetId" element={<Navigate to={ROUTES.portfolioAssets} replace />} />
          <Route path="/asset-management/asset-detail"       element={<Navigate to={ROUTES.portfolioAssets} replace />} />

          {/* ── Asset Management ───────────────────────────────────────────── */}
          <Route path={ROUTES.operationsConsole}  element={<OperationsConsole />} />
          <Route path={ROUTES.oversightHub}       element={<OversightHub />} />
          <Route path={ROUTES.reviewPacks}        element={<ReviewPacks />} />
          <Route path={ROUTES.riskMonitor}        element={<RiskMonitor />} />
          <Route path={ROUTES.valueCreation}      element={<ValueCreation />} />
          <Route path={ROUTES.watchlist}          element={<WatchlistActions />} />
          <Route path={ROUTES.reportBuilder}      element={<ReportBuilder />} />
          {/* Legacy redirects */}
          <Route path="/asset-management/operations-console"    element={<Navigate to={ROUTES.operationsConsole} replace />} />
          <Route path="/asset-management/oversight-hub"         element={<Navigate to={ROUTES.oversightHub} replace />} />
          <Route path="/asset-management/review-packs"          element={<Navigate to={ROUTES.reviewPacks} replace />} />
          <Route path="/asset-management/risk-monitor"          element={<Navigate to={ROUTES.riskMonitor} replace />} />
          <Route path="/asset-management/value-creation"        element={<Navigate to={ROUTES.valueCreation} replace />} />
          <Route path="/asset-management/watchlist-actions"     element={<Navigate to={ROUTES.watchlist} replace />} />

          {/* ── Operations ─────────────────────────────────────────────────── */}
          <Route path={ROUTES.dataRoom}           element={<DataRoom />} />
          <Route path={ROUTES.esgReporting}       element={<ESGReportingDashboard />} />
          <Route path={ROUTES.governanceRecords}  element={<GovernanceLibrary />} />
          <Route path={ROUTES.counterparties}     element={<CounterpartyDirectory />} />
          <Route path={ROUTES.auditTrail}         element={<AuditTrailExports />} />
          {/* Legacy redirects */}
          <Route path="/data-room"                element={<Navigate to={ROUTES.dataRoom} replace />} />
          <Route path="/esg-reporting"            element={<Navigate to={ROUTES.esgReporting} replace />} />
          <Route path="/esg-reporting-dashboard"  element={<Navigate to={ROUTES.esgReporting} replace />} />
          <Route path="/asset-management/governance-library"    element={<Navigate to={ROUTES.governanceRecords} replace />} />
          <Route path="/asset-management/counterparty-directory" element={<Navigate to={ROUTES.counterparties} replace />} />
          <Route path="/asset-management/audit-trail-exports"   element={<Navigate to={ROUTES.auditTrail} replace />} />

          {/* ── Transactions ───────────────────────────────────────────────── */}
          <Route path={ROUTES.tokenisation}       element={<RWATokenisationPage />} />
          <Route path={ROUTES.tokenHoldings}      element={<TokenHolderDashboard />} />
          <Route path={ROUTES.tokenTransfer}      element={<TokenTransferPage />} />
          <Route path={ROUTES.tokenAdmin}         element={<TokenAdminPage />} />
          {/* Token Governance sub-routes — all map to existing pages */}
          <Route path={ROUTES.tokenGovernance}    element={<GovernancePage />} />
          <Route path={ROUTES.govProposals}       element={<GovernancePage />} />
          <Route path={ROUTES.govAnalytics}       element={<GovernanceAnalyticsPage />} />
          <Route path={ROUTES.voteDelegation}     element={<VoteDelegationPage />} />
          <Route path={ROUTES.govCompliance}      element={<GovernanceCompliancePage />} />
          {/* Legacy redirects */}
          <Route path="/rwa-tokenisation"         element={<Navigate to={ROUTES.tokenisation} replace />} />
          <Route path="/token-holder-dashboard"   element={<Navigate to={ROUTES.tokenHoldings} replace />} />
          <Route path="/token-transfer"           element={<Navigate to={ROUTES.tokenTransfer} replace />} />
          <Route path="/token-admin"              element={<Navigate to={ROUTES.tokenAdmin} replace />} />
          <Route path="/governance"               element={<Navigate to={ROUTES.govProposals} replace />} />
          <Route path="/governance-analytics"     element={<Navigate to={ROUTES.govAnalytics} replace />} />
          <Route path="/vote-delegation"          element={<Navigate to={ROUTES.voteDelegation} replace />} />
          <Route path="/governance-compliance"    element={<Navigate to={ROUTES.govCompliance} replace />} />

          {/* ── Communications ─────────────────────────────────────────────── */}
          <Route path={ROUTES.messages}           element={<CommunicationsHub />} />
          <Route path={ROUTES.notifications}      element={<NotificationsPage />} />
          {/* Legacy redirects */}
          <Route path="/messages"                 element={<Navigate to={ROUTES.messages} replace />} />
          <Route path="/notifications"            element={<Navigate to={ROUTES.notifications} replace />} />
          <Route path="/communications-hub"       element={<Navigate to={ROUTES.messages} replace />} />

          {/* ── New MVP Modules (Role Framework Adaptation) ────────────────── */}
          <Route path={ROUTES.icSubmission}       element={<ICSubmissionPage />} />
          <Route path={ROUTES.dealRoom}           element={<DealRoomPage />} />
          <Route path={ROUTES.activityFeed}       element={<ActivityFeedPage />} />

          {/* ── Administration ─────────────────────────────────────────────── */}
          <Route path={ROUTES.teamManagement}     element={<TeamManagement />} />
          <Route path={ROUTES.settings}           element={<SettingsPage />} />
          <Route path={ROUTES.permissions}        element={<PermissionsPage />} />
          {/* Legacy redirects */}
          <Route path="/team-management"          element={<Navigate to={ROUTES.teamManagement} replace />} />
          <Route path="/settings"                 element={<Navigate to={ROUTES.settings} replace />} />

          {/* ── Catch-all ──────────────────────────────────────────────────── */}
          <Route path="*" element={<NotFound />} />

        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
    </RoleProvider>
  );
};

export default Routes;
